import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import RocketLaunchRounded from '@mui/icons-material/RocketLaunchRounded';
import SecurityRounded from '@mui/icons-material/SecurityRounded';
import BuildCircleRounded from '@mui/icons-material/BuildCircleRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { DefaultPage } from '../../layouts/default-page';

type ReleaseItem = {
  title: string;
  description: string;
  category: 'Feature' | 'Improvement' | 'Security' | 'Fix';
};

type Release = {
  version: string;
  date: string;
  isLatest?: boolean;
  summary: string;
  items: ReleaseItem[];
};

const getCategoryChip = (category: ReleaseItem['category']) => {
  switch (category) {
    case 'Feature':
      return (
        <Chip
          icon={<AutoAwesomeRounded sx={{ fontSize: '13px !important' }} />}
          label="New Feature"
          size="small"
          sx={{
            backgroundColor: '#EFF6FF',
            color: '#0072BC',
            fontWeight: 700,
            fontSize: 11.5,
            borderRadius: '4px',
            border: '1px solid #BFDBFE',
            height: 22,
          }}
        />
      );
    case 'Improvement':
      return (
        <Chip
          icon={<RocketLaunchRounded sx={{ fontSize: '13px !important' }} />}
          label="Improvement"
          size="small"
          sx={{
            backgroundColor: '#F0FDF4',
            color: '#16A34A',
            fontWeight: 700,
            fontSize: 11.5,
            borderRadius: '4px',
            border: '1px solid #BBF7D0',
            height: 22,
          }}
        />
      );
    case 'Security':
      return (
        <Chip
          icon={<SecurityRounded sx={{ fontSize: '13px !important' }} />}
          label="Security & Privacy"
          size="small"
          sx={{
            backgroundColor: '#FAF5FF',
            color: '#7E22CE',
            fontWeight: 700,
            fontSize: 11.5,
            borderRadius: '4px',
            border: '1px solid #E9D5FF',
            height: 22,
          }}
        />
      );
    case 'Fix':
      return (
        <Chip
          icon={<BuildCircleRounded sx={{ fontSize: '13px !important' }} />}
          label="Fix & Stability"
          size="small"
          sx={{
            backgroundColor: '#FFFBEB',
            color: '#D97706',
            fontWeight: 700,
            fontSize: 11.5,
            borderRadius: '4px',
            border: '1px solid #FDE68A',
            height: 22,
          }}
        />
      );
  }
};

const mobileAppReleases: Release[] = [
  {
    version: 'Version 2.3.0',
    date: 'October 2026',
    isLatest: true,
    summary:
      'Major update introducing our fresh modern mobile experience, digital cinema permit scanning, multimedia violation evidence submission, and faster performance.',
    items: [
      {
        title: 'Modern, Easy-to-Read Design',
        description:
          'Completely redesigned the entire app with a clean, modern look. Screens load faster, fonts are easier to read, and navigating between sections is smoother than ever.',
        category: 'Improvement',
      },
      {
        title: 'Digital Cinema Permit Verification (QR Scanner)',
        description:
          'Inspectors can now scan official cinema QR codes with their phone camera to instantly check if a movie theater exhibition certificate is genuine, active, and valid.',
        category: 'Feature',
      },
      {
        title: 'Photo & Video Evidence for Reports',
        description:
          'When submitting violation reports, you can now attach up to 4 photos and 1 short video clip directly from your phone camera or gallery to provide clear proof.',
        category: 'Feature',
      },
      {
        title: 'Official Monitoring Ticket Booking',
        description:
          'Deputies can easily request monitoring tickets on their phone. The app automatically checks your schedule to make sure movie times do not overlap or conflict.',
        category: 'Feature',
      },
      {
        title: 'Reviewed Movies & Classification Directory',
        description:
          'Browse currently reviewed movies with their official MTRCB ratings (G, PG, R-13, R-16, R-18) and theater listings with clean movie posters and summaries.',
        category: 'Feature',
      },
      {
        title: 'Smooth & Secure Password Reset',
        description:
          'Forgotten password requests can now be reset safely and directly from your mobile phone with verified account protection so you never get locked out.',
        category: 'Security',
      },
      {
        title: 'In-App Help & Issue Reporting',
        description:
          'If you experience an issue or have a suggestion, you can send a feedback report with a screenshot directly to the MTRCB IT support team right from the app menu.',
        category: 'Feature',
      },
    ],
  },
  {
    version: 'Version 2.2.0',
    date: 'September 2026',
    summary:
      'Added TV broadcast monitoring, public transport and venue monitoring, and board news advisories.',
    items: [
      {
        title: 'Television Broadcast Monitoring',
        description:
          'Deputies can log television programs, channels, and time slots to evaluate broadcast ratings and report unrated or non-compliant content.',
        category: 'Feature',
      },
      {
        title: 'Public Venues & Transit Monitoring',
        description:
          'Added dedicated reporting forms for public transport vehicles (buses, PUVs) and roadside promotional posters or publicity materials.',
        category: 'Feature',
      },
      {
        title: 'Official News & Board Events',
        description:
          'Read the latest news announcements, advisory guidelines, and upcoming board events directly inside the app.',
        category: 'Feature',
      },
    ],
  },
];

const adminConsoleReleases: Release[] = [
  {
    version: 'Version 2.4.0',
    date: 'October 07, 2026',
    isLatest: true,
    summary:
      'Complete system-wide modernization across all menus: unified search filters, full-database exports to Excel and PDF, page and filter memory when going back, and clean professional styling.',
    items: [
      {
        title: 'Full-Database Exporting (Excel & PDF) Across All Menus',
        description:
          'Export buttons now download 100% of all matching records across the entire database into Excel spreadsheets and official PDF documents—covering Users, Deputies, Staff, Malls, Theaters, Officials, Press Releases, News, and Events.',
        category: 'Feature',
      },
      {
        title: 'Unified Search & Filter Bar Everywhere',
        description:
          'Every directory in the administrative system now shares the same clean, easy-to-use search panel with quick search, one-click clear, and instant export buttons.',
        category: 'Improvement',
      },
      {
        title: 'Page & Filter Memory (Never Resets on Back)',
        description:
          'When you click any person, theater, or record to inspect details and click "Back", your search words, filters, and exact page number remain right where you left them.',
        category: 'Improvement',
      },
      {
        title: 'Export Progress Protection Screen',
        description:
          'When downloading large reports, a friendly screen message appears showing real-time progress while protecting buttons from accidental double-clicks until your file is ready.',
        category: 'Improvement',
      },
      {
        title: 'Clean, Sharp, and Professional Styling',
        description:
          'Purged oversized bubbly curves across all popups and tables. All buttons, input fields, cards, and dialogs now feature crisp, modern, and professional borders.',
        category: 'Improvement',
      },
      {
        title: 'Executive Login & Security Update',
        description:
          'Refreshed the administrative sign-in portal with official MTRCB executive branding, official board seals, and enhanced Data Privacy Act compliance advisories.',
        category: 'Security',
      },
    ],
  },
  {
    version: 'Version 2.3.0',
    date: 'September 2026',
    summary:
      'Enhanced user administration, interactive notification center, and deputy performance trend analytics.',
    items: [
      {
        title: 'Interactive Notification Bell',
        description:
          'A live notification menu at the top of the screen that immediately alerts you when new cinema permits, citizen complaints, or violation reports are submitted.',
        category: 'Feature',
      },
      {
        title: 'User & Deputy Management Hub',
        description:
          'Easily manage administrative accounts, MTRCB staff, and certified deputies with clear buttons to view details, update login emails, or reset passwords.',
        category: 'Improvement',
      },
      {
        title: 'Deputy Performance Analytics',
        description:
          'View real-time visual charts tracking monthly report submissions, active inspection trends, and endorser quotas.',
        category: 'Feature',
      },
    ],
  },
];

export default function SoftwareUpdatesPage() {
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const currentReleases = tabIndex === 0 ? mobileAppReleases : adminConsoleReleases;

  return (
    <DefaultPage
      title="Software Updates"
      subTitle="View the latest improvements, new features, and user-friendly release notes."
    >
      {/* Information Banner */}
      <Box mb={2.5}>
        <Card
          elevation={0}
          sx={{
            border: '1px solid #E0F2FE',
            backgroundColor: '#F0F9FF',
            borderRadius: '6px',
            p: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <InfoOutlined sx={{ color: '#0072BC', fontSize: 24, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0369A1' }}>
                About MTRCB System Releases
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#334155', mt: 0.2 }}>
                Release notes are written in clear, non-technical language so board members,
                executives, administrative staff, and deputies can easily see what was improved.
              </Typography>
            </Box>
          </Stack>
        </Card>
      </Box>

      {/* Main Tabbed Container */}
      <Card
        elevation={0}
        sx={{
          borderRadius: '6px',
          border: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: '#E2E8F0', px: 2, pt: 1 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="software updates tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 650,
                fontSize: 14,
                minHeight: 48,
                color: '#64748B',
                '&.Mui-selected': {
                  color: '#0072BC',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0072BC',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              icon={<PhoneIphoneRounded sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="MTRCB Mobile App"
            />
            <Tab
              icon={<AdminPanelSettingsRounded sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Admin Web Portal"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            {currentReleases.map((release) => (
              <Card
                key={release.version}
                elevation={0}
                sx={{
                  border: release.isLatest ? '1.5px solid #0072BC' : '1px solid #E2E8F0',
                  borderRadius: '6px',
                  backgroundColor: release.isLatest ? '#FAFDFF' : '#FFFFFF',
                  overflow: 'hidden',
                }}
              >
                {/* Release Card Header */}
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: release.isLatest ? '#F0F9FF' : '#F8FAFC',
                    borderBottom: '1px solid #E2E8F0',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>
                        {release.version}
                      </Typography>
                      {release.isLatest && (
                        <Chip
                          label="LATEST UPDATE"
                          size="small"
                          sx={{
                            backgroundColor: '#0072BC',
                            color: '#FFFFFF',
                            fontWeight: 800,
                            fontSize: 11,
                            borderRadius: '3px',
                            height: 22,
                          }}
                        />
                      )}
                    </Stack>

                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#64748B' }}>
                      Released: {release.date}
                    </Typography>
                  </Stack>

                  <Typography sx={{ fontSize: 13.5, color: '#475569', mt: 1, lineHeight: 1.5 }}>
                    {release.summary}
                  </Typography>
                </Box>

                {/* Release Items List */}
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={2} divider={<Divider sx={{ borderColor: '#F1F5F9' }} />}>
                    {release.items.map((item, itemIdx) => (
                      <Stack
                        key={itemIdx}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                      >
                        <Box sx={{ mt: 0.3 }}>
                          <CheckCircleRounded sx={{ color: '#00934C', fontSize: 18 }} />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" mb={0.4}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>
                              {item.title}
                            </Typography>
                            {getCategoryChip(item.category)}
                          </Stack>

                          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      </Card>
    </DefaultPage>
  );
}
